import { API_BASE_URL } from './utils';
import {
  getAllQueued,
  removeAction,
  incrementRetry,
  type QueuedAction,
} from './offlineQueue';

export const ACTION_QUEUE_CHANGED_EVENT = 'c2:action-queue-changed';

const MAX_RETRIES = 8;

function notifyQueueChanged(): void {
  window.dispatchEvent(new CustomEvent(ACTION_QUEUE_CHANGED_EVENT));
}

function getToken(): string | null {
  return localStorage.getItem('c2_token');
}

async function replayAction(action: QueuedAction, token: string): Promise<boolean> {
  try {
    switch (action.type) {
      case 'status_change': {
        const { resourceId, status } = action.payload;
        const res = await fetch(`${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status }),
        });
        return res.ok;
      }

      case 'chat_message': {
        const { channelType, targetUserId, groupId, text, eventId, from, fromName } = action.payload;
        const res = await fetch(`${API_BASE_URL}/chat/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            channelType,
            targetUserId,
            groupId,
            text,
            eventId,
            from,
            fromName,
            offlineQueued: true,
          }),
        });
        return res.ok;
      }

      case 'incident_note': {
        const { incidentId, details, actionLabel } = action.payload;
        const res = await fetch(`${API_BASE_URL}/incidents/${encodeURIComponent(incidentId)}/logs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ details, action: actionLabel || 'Note', offlineQueued: true }),
        });
        return res.ok;
      }

      case 'api_request': {
        const { method, path, body } = action.payload as {
          method: string;
          path: string;
          body?: unknown;
        };
        const res = await fetch(`${API_BASE_URL}${path}`, {
          method,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: body != null ? JSON.stringify(body) : undefined,
        });
        return res.ok;
      }

      default:
        return true;
    }
  } catch {
    return false;
  }
}

let processing = false;

export async function processActionQueue(): Promise<number> {
  if (processing || !navigator.onLine) return 0;

  const token = getToken();
  if (!token) return 0;

  processing = true;
  let synced = 0;

  try {
    const queued = await getAllQueued();
    if (queued.length === 0) return 0;

    for (const action of queued) {
      if (action.retries >= MAX_RETRIES) {
        await removeAction(action.id);
        continue;
      }

      const success = await replayAction(action, token);
      if (success) {
        await removeAction(action.id);
        synced += 1;
      } else {
        await incrementRetry(action.id);
      }
    }
  } finally {
    processing = false;
    notifyQueueChanged();
  }

  return synced;
}
